import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import { getNonProductionBuildAnalyticsAttributes } from '@atlaskit/editor-common/utils/is-non-production-build';

import { getIsInConfluenceHoldoutData } from '@atlassian/holdout-utils/confluence-holdout';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { CollabServiceProperty } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

import {
	extendPayloadAttributes,
	extractAttributesFromContext,
	getProseMirrorFMPTimestamp,
} from './helpers.ts';
import { getConfluenceNonProductionBuild } from './isNonProductionBuild.ts';

interface EditorAnalyticsListenerProps {
	contentId?: string;
	contentType?: string;
	source?: string;
	collabService?: CollabServiceProperty;
	children?: React.ReactNode;
	onProseMirrorRendered?: Function;
	isGuest?: boolean;
	touchpointId?: string;
}

const handleOnProseMirrorRendered = (onProseMirrorRendered, eventAttributes) => {
	if (onProseMirrorRendered && typeof onProseMirrorRendered === 'function') {
		const clientRouteStart = performance.getEntriesByName('browser-metric.start').pop()?.startTime;
		const proseMirrorRenderedTimestamp = getProseMirrorFMPTimestamp(
			eventAttributes?.startTime,
			eventAttributes?.duration,
		);
		const proseMirrorRenderedFMP =
			typeof clientRouteStart !== 'undefined' && typeof proseMirrorRenderedTimestamp !== 'undefined'
				? Math.round(proseMirrorRenderedTimestamp - clientRouteStart)
				: proseMirrorRenderedTimestamp;
		onProseMirrorRendered({
			...eventAttributes,
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
		});
	}
};

export const EditorAnalyticsListener: FunctionComponent<EditorAnalyticsListenerProps> = (props) => {
	const isLivePage = useIsCurrentPageLive();
	const spaceId = useSpaceId();
	const { edition, environment } = useSessionData() || {};
	// setting default to development so in tests it doesn't fail due to being undefined
	const safeEnvironment = environment || 'DEVELOPMENT';
	const renderServerPlaceholder = useIsSSROrHydrating();
	const holdoutAnalyticsAttributesRef = useRef<{ holdout: string } | null>(null);
	const getCachedHoldoutAnalyticsAttributes = useCallback(() => {
		if (holdoutAnalyticsAttributesRef.current === null) {
			holdoutAnalyticsAttributesRef.current = getIsInConfluenceHoldoutData();
		}
		return holdoutAnalyticsAttributesRef.current;
	}, []);
	const nonProductionBuildAnalyticsAttributes = useMemo(
		() =>
			getNonProductionBuildAnalyticsAttributes(() =>
				renderServerPlaceholder ? undefined : getConfluenceNonProductionBuild(),
			),
		[renderServerPlaceholder],
	);
	const {
		contentId,
		contentType,
		children,
		source,
		collabService,
		onProseMirrorRendered,
		isGuest,
		touchpointId,
	} = props;

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const instanceName: string | undefined = safeEnvironment;
			if (event?.payload?.action === 'proseMirrorRendered') {
				handleOnProseMirrorRendered(onProseMirrorRendered, event?.payload?.attributes);
			}

			event.update((payload) => {
				const isCommentEvent = payload.attributes && payload.attributes.appearance === 'comment';

				// WS-3190 If this is a comment event use the existing objectId as it correlates to the commentId
				let newPayload = extendPayloadAttributes(payload, {
					objectId: isCommentEvent ? payload.attributes.objectId : contentId,
					editorSource: source,
				}) as AnalyticsEventPayload;

				if (instanceName) {
					newPayload = extendPayloadAttributes(newPayload, {
						instance: instanceName,
					});
				}

				newPayload = extendPayloadAttributes(newPayload, {
					collabService: collabService ?? 'none',
					...(fg('confluence_editor_content_type_analytics') && { contentType }),
					isLivePage,
					edition,
					spaceId,
					...(expValEquals('platform_editor_holdout_analytics', 'isEnabled', true) &&
						getCachedHoldoutAnalyticsAttributes()),
				});

				if (isGuest !== undefined) {
					newPayload = extendPayloadAttributes(newPayload, {
						isGuest,
					});
				}

				if (touchpointId) {
					newPayload = extendPayloadAttributes(newPayload, {
						touchpointId,
					});
				}

				newPayload.source = source || 'unknown';

				if (event.context) {
					const attributesFromContext = extractAttributesFromContext(event.context);
					newPayload.attributes = {
						...newPayload.attributes,
						...attributesFromContext,
					};
				}

				if (nonProductionBuildAnalyticsAttributes.isNonProductionBuild !== undefined) {
					newPayload = extendPayloadAttributes(newPayload, nonProductionBuildAnalyticsAttributes);
				}

				return newPayload;
			});
		},
		[
			contentId,
			contentType,
			source,
			collabService,
			onProseMirrorRendered,
			edition,
			safeEnvironment,
			isLivePage,
			spaceId,
			isGuest,
			touchpointId,
			nonProductionBuildAnalyticsAttributes,
			getCachedHoldoutAnalyticsAttributes,
		],
	);

	const handleFabricElementsEvents = useCallback(
		(event: UIAnalyticsEvent) => {
			event.update((payload) => {
				let newPayload = extendPayloadAttributes(payload, {
					pageType: contentType,
					editorSource: source,
					objectId: contentId,
					objectType: contentType,
					spaceId,
					isLivePage,
				});
				if (nonProductionBuildAnalyticsAttributes.isNonProductionBuild !== undefined) {
					newPayload = extendPayloadAttributes(newPayload, nonProductionBuildAnalyticsAttributes);
				}
				return newPayload;
			});
		},
		[contentId, contentType, source, isLivePage, spaceId, nonProductionBuildAnalyticsAttributes],
	);

	return (
		<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.editor}>
			<AnalyticsListener onEvent={handleFabricElementsEvents} channel={FabricChannel.elements}>
				<AnalyticsListener onEvent={handleFabricElementsEvents} channel={FabricChannel.media}>
					{children}
				</AnalyticsListener>
			</AnalyticsListener>
		</AnalyticsListener>
	);
};
